<template>
    <div class="ui-page">
        <ui-page-header title="Casos" :subtitle="current.description">
            <v-btn outlined @click="openSummary"><v-icon left small>mdi-message-text-outline</v-icon>Resumo do dia</v-btn>
            <v-btn v-if="$allowed('prospect.import')" color="primary" depressed @click="setTab('importacoes')"><v-icon left small>mdi-table-arrow-up</v-icon>Importar planilha</v-btn>
        </ui-page-header>

        <div class="ui-list-bar">
            <ui-pills :value="tab" :items="tabItems" @input="setTab" />
        </div>

        <case-table v-if="current.scope" :key="tab" :scope="current.scope" :tab="tab" :initial-status="initialStatus" @counts="setCounts" />
        <case-pending-board v-else-if="tab === 'acompanhamento'" key="acompanhamento" />
        <case-imports v-else key="importacoes" />

        <v-dialog v-model="summary.open" max-width="640">
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Resumo do dia</h3>
                        <p class="ui-section-description">Texto pronto para o grupo do squad. Considera os casos importados na data (o dia em que entraram no sistema), exceto os descartados.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-text-field v-model="summary.date" type="date" label="Data da importação" outlined dense hide-details class="mb-4" style="max-width: 220px;"></v-text-field>
                    <v-textarea :value="summary.text" outlined readonly rows="9" auto-grow hide-details :loading="summary.loading" class="pcs-summary"></v-textarea>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined @click="summary.open = false">Fechar</v-btn>
                    <v-btn outlined :disabled="!summary.items.length" @click="downloadSummary"><v-icon left small>mdi-microsoft-excel</v-icon>Baixar planilha (.xlsx)</v-btn>
                    <v-btn color="primary" depressed :disabled="!summary.text" @click="copySummary"><v-icon left small>mdi-content-copy</v-icon>Copiar para o WhatsApp</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import CaseTable from "@/components/prospect/cases/CaseTable.vue";
import CasePendingBoard from "@/components/prospect/cases/CasePendingBoard.vue";
import CaseImports from "@/components/prospect/cases/CaseImports.vue";
import { downloadXlsx } from "@/common/util/export-xlsx.util";
import { formatDocument, formatFeedDate, notifyError, notifySuccess, todayKey } from "@/components/prospect/prospect.util";

// Abas da tela de Casos, na ordem do trabalho: o que chegou e falta triar, o
// que esta em acompanhamento (lista de sexta), o que encerrou, tudo e as
// importacoes das planilhas. A aba fica na URL (?aba=) para voltar da ficha.
const TABS = [
    { value: "a-triar", text: "A triar", scope: "triage", count: "triage", tone: "warning",
        description: "Casos que chegaram pelas importações e ainda não foram triados. Abre com o passivo acima de R$ 10 mi (troque a faixa no filtro Passivo); marque acompanhar, passivo baixo ou descartar." },
    { value: "acompanhamento", text: "Em acompanhamento", count: "open",
        description: "Lista da reunião de sexta: casos abertos com AJ pendente, por UF e em ordem de passivo, com a etapa BeJ e os últimos movimentos." },
    { value: "encerrados", text: "Encerrados", scope: "closed", count: "closed",
        description: "Casos descartados, encerrados sem RJ ou com o AJ já definido (BeJ ou concorrente)." },
    { value: "todos", text: "Todos", scope: "all", count: "all",
        description: "Todos os casos captados, em qualquer etapa. A busca pelo número do processo também acha o que foi descartado na importação." },
    { value: "importacoes", text: "Importações",
        description: "Importe o e-mail diário do RecuperaJur ou a planilha BRZJ. A plataforma separa RJ, TCA e recuperação extrajudicial do resto e você confere a prévia antes de confirmar." },
];

export default {
    name: "ProspectCases",

    components: { CaseTable, CasePendingBoard, CaseImports },

    data: () => ({
        counts: null,
        summary: { open: false, date: "", text: "", items: [], loading: false },
    }),

    computed: {
        tab() {
            const value = this.$route.query.aba;
            if (TABS.some((item) => item.value === value)) return value;
            // Link antigo com ?status=: mostra em "Todos" ja filtrado.
            return this.$route.query.status ? "todos" : "a-triar";
        },
        current() {
            return TABS.find((item) => item.value === this.tab);
        },
        initialStatus() {
            return this.tab === "todos" ? this.$route.query.status || null : null;
        },
        tabItems() {
            return TABS.map((item) => ({
                value: item.value,
                text: item.text,
                tone: item.tone,
                count: this.counts && item.count ? this.counts[item.count] : undefined,
            }));
        },
    },

    watch: {
        // Ao trocar de aba, as contagens podem ter mudado (triagem, nomeacoes).
        tab() {
            this.loadCounts();
        },
        "summary.date"(value, previous) {
            if (this.summary.open && previous && value) this.loadSummary();
        },
    },

    created() {
        this.loadCounts();
    },

    methods: {
        setTab(value) {
            if (value === this.tab) return;
            this.$router.replace({ path: "/prospeccao/casos", query: { aba: value } });
        },

        setCounts(counts) {
            if (counts) this.counts = counts;
        },

        async loadCounts() {
            try {
                const result = await axios.get("/api/admin/prospect/cases", { params: { perPage: 1 } });
                this.setCounts(result.data.content.scope_counts);
            } catch (error) {
                // As contagens sao so informativas; a aba carrega por conta propria.
            }
        },

        openSummary() {
            this.summary = { open: true, date: todayKey(), text: "", items: [], loading: false };
            this.loadSummary();
        },

        async loadSummary() {
            this.summary.loading = true;
            try {
                const result = await axios.get("/api/admin/prospect/daily-summary", { params: { date: this.summary.date } });
                this.summary.text = result.data.content.text;
                this.summary.items = result.data.content.items || [];
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.summary.loading = false;
            }
        },

        // Os mesmos casos do texto, um por linha.
        downloadSummary() {
            downloadXlsx(this.summary.items.map((item) => ({
                Processo: item.process_number,
                Tipo: item.type_label,
                Empresa: item.company_name || "",
                CNPJ: formatDocument(item.company_document),
                UF: item.uf || "",
                Cidade: item.city || "",
                Vara: item.court_name || "",
                Juiz: item.judge_name || "",
                Passivo: item.debt_amount,
                "Valor da causa": item.claim_amount,
                "Passivo baixo": item.below_threshold || item.pipeline_status === "passivo_baixo" ? "Sim" : "",
                Etapa: item.pipeline_status_label,
                Dono: item.owner ? item.owner.name : "",
                Distribuição: formatFeedDate(item.filed_at),
                Cadastro: formatFeedDate(item.captured_at),
                Importado: this.$options.filters.defaultDate(item.created_at, "DD/MM/YYYY HH:mm"),
                Fonte: item.source_label,
            })), `resumo-do-dia-${this.summary.date}`, { sheetName: "Resumo do dia" });
        },

        async copySummary() {
            try {
                await navigator.clipboard.writeText(this.summary.text);
                notifySuccess(this, "Resumo copiado.");
            } catch (error) {
                notifyError(this, error);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.pcs-summary ::v-deep textarea {
    font-size: 13px;
    line-height: 1.5;
}
</style>
